import React from 'react';
import { Plus, Edit, Trash2, Search, MessageSquare } from 'lucide-react';
import { useCrud, useModal, useForm } from '../hooks';
import { ocorrenciaService, eventoService, ocorrenciaTipoService } from '../services/api';

const Ocorrencias = () => {
  const { data: ocorrencias, loading, error, create, update, remove } = useCrud(ocorrenciaService, 'ocorrências');
  const { data: eventos } = useCrud(eventoService, 'eventos');
  const { data: tipos } = useCrud(ocorrenciaTipoService, 'tipos de ocorrências');
  const modal = useModal();
  const [searchTerm, setSearchTerm] = React.useState('');

  const validationRules = {
    data_abertura: { required: true, label: 'Data de Abertura' },
    status: { required: true, label: 'Status' },
    descricao: { required: false, label: 'Descrição' },
    nome_solicitante: { required: false, label: 'Nome do Solicitante' },
    nome_conclusao: { required: false, label: 'Nome da Conclusão' },
    evento_id: { required: false, label: 'Evento' },
    tipo_id: { required: false, label: 'Tipo' },
    observacoes: { required: false, label: 'Observações' }
  };

  const form = useForm(
    {
      data_abertura: new Date().toISOString().split('T')[0],
      status: 'ABERTO',
      descricao: '',
      nome_solicitante: '',
      nome_conclusao: '',
      evento_id: '',
      tipo_id: '',
      observacoes: ''
    },
    validationRules
  );

  const filteredOcorrencias = ocorrencias.filter(ocorrencia =>
    ocorrencia.descricao?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ocorrencia.nome_solicitante?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ocorrencia.OcorrenciaTipo?.nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    form.reset();
    modal.open({ mode: 'create', title: 'Nova Ocorrência' });
  };

  const handleEdit = (ocorrencia) => {
    const editData = {
      data_abertura: ocorrencia.data_abertura ? ocorrencia.data_abertura.split('T')[0] : new Date().toISOString().split('T')[0],
      status: ocorrencia.status || 'ABERTO',
      descricao: ocorrencia.descricao || '',
      nome_solicitante: ocorrencia.nome_solicitante || '',
      nome_conclusao: ocorrencia.nome_conclusao || '',
      evento_id: ocorrencia.evento_id || '',
      tipo_id: ocorrencia.tipo_id || '',
      observacoes: ocorrencia.observacoes || ''
    };

    // Adicionar data_fechamento apenas se existir
    if (ocorrencia.data_fechamento) {
      editData.data_fechamento = ocorrencia.data_fechamento.split('T')[0];
    }

    form.reset(editData);
    modal.open({ mode: 'edit', title: 'Editar Ocorrência', id: ocorrencia.id });
  };

  const handleDelete = async (ocorrencia) => {
    if (window.confirm(`Deseja realmente excluir esta ocorrência?`)) {
      const result = await remove(ocorrencia.id);
      if (result.success) {
        // Success handled by the hook
      }
    }
  };

  const handleSubmit = async (values) => {
    // Limpar campos de data vazios para evitar 'Invalid date'
    const cleanedValues = { ...values };
    
    // Se data_fechamento estiver vazia, remover o campo
    if (!cleanedValues.data_fechamento || cleanedValues.data_fechamento === '') {
      delete cleanedValues.data_fechamento;
    }
    
    // Converter campos vazios para null para campos opcionais
    if (!cleanedValues.evento_id || cleanedValues.evento_id === '') {
      cleanedValues.evento_id = null;
    }
    if (!cleanedValues.tipo_id || cleanedValues.tipo_id === '') {
      cleanedValues.tipo_id = null;
    }

    const result = modal.data.mode === 'create' 
      ? await create(cleanedValues)
      : await update(modal.data.id, cleanedValues);

    if (result.success) {
      modal.close();
      form.reset();
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ABERTO':
        return 'bg-yellow-100 text-yellow-800';
      case 'EM_ANDAMENTO':
        return 'bg-blue-100 text-blue-800';
      case 'RESOLVIDO':
        return 'bg-green-100 text-green-800';
      case 'FECHADO':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Ocorrências</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={20} />
          Nova Ocorrência
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar ocorrências..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input pl-10"
            />
          </div>
        </div>
      </div>

      <div className="card">
        {filteredOcorrencias.length === 0 ? (
          <div className="empty-state">
            <MessageSquare className="empty-state-icon" />
            <h3 className="text-lg font-medium mb-2">Nenhuma ocorrência encontrada</h3>
            <p className="mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando uma nova ocorrência.'}
            </p>
            {!searchTerm && (
              <button onClick={handleCreate} className="btn btn-primary">
                <Plus size={20} />
                Nova Ocorrência
              </button>
            )}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Solicitante</th>
                  <th>Descrição</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredOcorrencias.map((ocorrencia) => (
                  <tr key={ocorrencia.id}>
                    <td>{formatDateTime(ocorrencia.data_abertura)}</td>
                    <td>{ocorrencia.OcorrenciaTipo?.nome || 'N/A'}</td>
                    <td>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ocorrencia.status)}`}>
                        {ocorrencia.status?.replace('_', ' ') || 'N/A'}
                      </span>
                    </td>
                    <td>{ocorrencia.nome_solicitante || '-'}</td>
                    <td className="max-w-xs truncate">{ocorrencia.descricao || '-'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(ocorrencia)}
                          className="btn btn-sm btn-secondary"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(ocorrencia)}
                          className="btn btn-sm btn-danger"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal.isOpen && (
        <div className="modal-overlay" onClick={modal.close}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">{modal.data.title}</h2>
              <button onClick={modal.close} className="modal-close">×</button>
            </div>

            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="form-group">
                <label className="form-label">Data de Abertura *</label>
                <input
                  type="date"
                  value={form.values.data_abertura}
                  onChange={(e) => form.setValue('data_abertura', e.target.value)}
                  onBlur={() => form.setTouched('data_abertura')}
                  className={`form-input ${form.errors.data_abertura && form.touched.data_abertura ? 'border-red-500' : ''}`}
                />
                {form.errors.data_abertura && form.touched.data_abertura && (
                  <div className="form-error">{form.errors.data_abertura}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status *</label>
                <select
                  value={form.values.status}
                  onChange={(e) => form.setValue('status', e.target.value)}
                  onBlur={() => form.setTouched('status')}
                  className={`form-select ${form.errors.status && form.touched.status ? 'border-red-500' : ''}`}
                >
                  <option value="">Selecione o status</option>
                  <option value="ABERTO">Aberto</option>
                  <option value="EM_ANDAMENTO">Em Andamento</option>
                  <option value="RESOLVIDO">Resolvido</option>
                  <option value="FECHADO">Fechado</option>
                </select>
                {form.errors.status && form.touched.status && (
                  <div className="form-error">{form.errors.status}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Evento</label>
                <select
                  value={form.values.evento_id}
                  onChange={(e) => form.setValue('evento_id', e.target.value)}
                  className="form-select"
                >
                  <option value="">Selecione um evento</option>
                  {eventos.map((evento) => (
                    <option key={evento.id} value={evento.id}>
                      {evento.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Tipo</label>
                <select
                  value={form.values.tipo_id}
                  onChange={(e) => form.setValue('tipo_id', e.target.value)}
                  className="form-select"
                >
                  <option value="">Selecione um tipo</option>
                  {tipos.map((tipo) => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Nome do Solicitante</label>
                <input
                  type="text"
                  value={form.values.nome_solicitante}
                  onChange={(e) => form.setValue('nome_solicitante', e.target.value)}
                  className="form-input"
                  placeholder="Digite o nome do solicitante"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Descrição</label>
                <textarea
                  value={form.values.descricao}
                  onChange={(e) => form.setValue('descricao', e.target.value)}
                  className="form-input"
                  placeholder="Descreva a ocorrência"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Observações</label>
                <textarea
                  value={form.values.observacoes}
                  onChange={(e) => form.setValue('observacoes', e.target.value)}
                  className="form-input"
                  placeholder="Observações adicionais"
                  rows="2"
                />
              </div>

              <div className="modal-footer">
                <button type="button" onClick={modal.close} className="btn btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {modal.data.mode === 'create' ? 'Criar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Ocorrencias;